/*
 * The Route view of the Schedule (on when routes are on, Settings, Schedule): one day, each route's stops in driving order
 * beside a map with a line through them. It replaces the old Route tab (owner, 2026-10-29), which was a weekday picker
 * apart from the Schedule, had no map, left one-off jobs off, and moved stops one tap at a time.
 *
 * - A route's stops keep their place on the route (the job's place), so a new order holds every week. Stops on no route
 *   keep the day's order. Drag a stop or use its up and down arrows to change its place; Best order finds the shortest
 *   drive and shows the minutes, miles and gas it saves before anything changes.
 * - The map line follows the roads when Google can find them (route-path, solid), from where the teams start (house pin)
 *   and back; straight dashes stop to stop otherwise.
 * - Skip this time (a stop's menu) skips that one visit, with Undo; the job stays on the route.
 * - Route people work every stop on the route unless a job or visit has its own people (as the old Steward).
 * - Moving a route's day is this time only; the route stays on its weekday, and clients are texted only when the owner
 *   writes the text.
 * - Open in Google Maps gives the drive in legs of up to 10 stops (Google's limit), from where the teams start.
 */
import { useNavigate } from "@tanstack/react-router";
import { useState, type DragEvent } from "react";
import {
  addDays,
  isOffSchedule,
  routesOn,
  weekday,
  weekParity,
  gallonsFor,
  type BestOrderAnswer,
  type RouteInput,
  type RouteRow,
  type VisitCard,
} from "@steward/shared";
import {
  useAddRoute,
  useBestOrder,
  useRoutePaths,
  useChangeRoute,
  useDayOrder,
  useMoveRoute,
  useOrderRoute,
  usePeople,
  useRemoveRoute,
  useRoutes,
  useSaveDayOrder,
  useSettings,
  useVisits,
  useVisitStatus,
} from "../api/hooks";
import { useSession } from "../app/session";
import { InputText } from "../components/fields";
import { dayDate, money, plural, shortDate, timeRange } from "../components/format";
import { Icon } from "../components/Icon";
import { Menu, Modal, useToast } from "../components/overlays";
import { Banner, Button, Chip, EmptyState, IconButton, LoadError, Skeleton, StatusLabel } from "../components/parts";
import { VisitMap, type MapLine } from "../components/VisitMap";
import { errText } from "../components/work";
import { DayText } from "./Field";

export const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
/** The old Steward's six route colors, in order. A new route takes the next one. */
export const ROUTE_COLORS = ["#367C2B", "#0A84FF", "#BF5AF2", "#FF9F0A", "#FF375F", "#30B0C7"];
const OFF_ROUTE = "#8E8E93";

/** 1 Monday to 7 Sunday. */
export const weekdayNumber = (d: string) => ((weekday(d) + 6) % 7) + 1;
/** The first day on or after d that falls on a weekday (1 Monday to 7 Sunday). */
export const nextOn = (d: string, wd: number) => addDays(d, (wd - weekdayNumber(d) + 7) % 7);
/** A route's next run on or after d: its weekday, and its own week when it takes turns with another route. */
export const nextRun = (d: string, r: Pick<RouteRow, "weekday" | "week">) => {
  const n = nextOn(d, r.weekday);
  return r.week === null || weekParity(n) === r.week ? n : addDays(n, 7);
};
export const routeColor = (r: Pick<RouteRow, "color"> | undefined, i = 0) => r?.color ?? ROUTE_COLORS[i % ROUTE_COLORS.length]!;

/** Is the Route view on for this company? */
export function useRoutesOn(): boolean {
  const { company } = useSession();
  const s = useSettings(company.id);
  return s.data ? routesOn(s.data.settings, company.trade) : false;
}

/**
 * The route's whole order (every job on it) after some of its stops changed places: the stops keep the slots they held,
 * in their new order, and every other job keeps its own slot. So reordering Saturday's stops does not move the jobs that
 * only come every other week.
 */
export function mergeOrder(all: readonly string[], moved: readonly string[]): string[] {
  const set = new Set(moved);
  const next = [...moved];
  const out = all.map((id) => (set.has(id) ? next.shift()! : id));
  return [...out, ...next];
}

/**
 * A route's whole order when a day's stops include weekly jobs riding with it from a route that takes turns with it
 * (route-turns.ts): the route's own jobs as mergeOrder puts them, and each rider just before the stop it was dropped
 * above, so the server keeps that as the rider's place for this week only.
 */
export function withRiders(own: readonly string[], dayOrder: readonly string[]): string[] {
  const mine = new Set(own);
  const out = mergeOrder(
    own,
    dayOrder.filter((id) => mine.has(id)),
  );
  for (const [k, id] of dayOrder.entries()) {
    if (mine.has(id)) continue;
    const next = dayOrder.slice(k + 1).find((x) => mine.has(x));
    out.splice(next ? out.indexOf(next) : out.length, 0, id);
  }
  return out;
}

/** Google Maps links for the drive, in legs of up to 10 stops (an origin, 8 or 9 stops between and a destination). */
export function mapsLinks(start: string, stops: readonly VisitCard[]): { label: string; href: string }[] {
  const at = (v: VisitCard) => (v.lat !== null && v.lng !== null ? `${String(v.lat)},${String(v.lng)}` : (v.address ?? ""));
  const places = stops.map(at).filter(Boolean);
  if (!places.length) return [];
  const out: { label: string; href: string }[] = [];
  let from = start || null;
  for (let i = 0; i < places.length;) {
    const take = from ? 9 : 10;
    const chunk = places.slice(i, i + take);
    const origin = from ?? chunk.shift()!;
    const dest = chunk.pop() ?? origin;
    const q = new URLSearchParams({ api: "1", origin, destination: dest, travelmode: "driving" });
    if (chunk.length) q.set("waypoints", chunk.join("|"));
    const first = from && i === 0 ? 1 : i + 1;
    out.push({
      label: places.length <= take ? "Open in Google Maps" : `Maps, stops ${String(first)} to ${String(i + take)}`,
      href: `https://www.google.com/maps/dir/?${q.toString()}`,
    });
    from = dest;
    i += take;
  }
  return out;
}

interface Group {
  key: string;
  route: RouteRow | null;
  color: string;
  stops: VisitCard[];
}

export function RouteDay({ date, onDate }: { date: string; onDate: (d: string) => void }) {
  const { company, can } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const edit = can("schedule.update");
  const visits = useVisits(company.id, { from: date, to: date });
  const routes = useRoutes();
  const order = useDayOrder(date);
  const settings = useSettings(company.id);
  const people = usePeople(company.id, edit);
  const orderRoute = useOrderRoute();
  const saveDay = useSaveDayOrder();
  const status = useVisitStatus();
  const [hover, setHover] = useState<string | null>(null);
  const [managing, setManaging] = useState<RouteRow | "new" | "list" | null>(null);
  const [moving, setMoving] = useState<RouteRow | null>(null);
  const [texting, setTexting] = useState<RouteRow | null>(null);
  const [drag, setDrag] = useState<{ group: string; ref: string } | null>(null);

  if (visits.error) return <LoadError error={visits.error} retry={() => void visits.refetch()} />;
  if (!visits.data || !routes.data) return <Skeleton rows={6} />;

  const all = routes.data.routes;
  const live = all.filter((r) => r.active);
  const byId = new Map(all.map((r, i) => [r.id, { r, i }]));
  const dayAt = new Map((order.data?.refs ?? []).map((r, i) => [r, i]));
  const stops = visits.data.visits.filter((v) => v.date === date && !isOffSchedule(v.status));
  const wd = weekdayNumber(date);
  const start = settings.data?.settings.field.startAddress ?? "";

  // Routes with stops today, then routes that run today with none; stops on no route last.
  const groups: Group[] = [];
  for (const r of all) {
    const here = stops.filter((v) => v.routeId === r.id);
    // A route that takes turns (its own week) shows empty only on its own weeks.
    if (!here.length && !(r.active && r.weekday === wd && (r.week === null || r.week === weekParity(date)))) continue;
    const place = new Map(r.jobs.map((j, i) => [j.id, i]));
    // The server's order for the day first: it knows a weekly rider's place for this week.
    here.sort(
      (a, b) =>
        (dayAt.get(a.ref) ?? 9999) - (dayAt.get(b.ref) ?? 9999) ||
        (place.get(a.jobId) ?? 9999) - (place.get(b.jobId) ?? 9999) ||
        (a.startTime ?? "99").localeCompare(b.startTime ?? "99"),
    );
    groups.push({ key: r.id, route: r, color: routeColor(r, byId.get(r.id)?.i), stops: here });
  }
  groups.sort(
    (a, b) => Number(b.stops.length > 0) - Number(a.stops.length > 0) || (a.route?.name ?? "").localeCompare(b.route?.name ?? ""),
  );
  const loose = stops
    .filter((v) => !v.routeId || !byId.has(v.routeId))
    .sort(
      (a, b) =>
        (dayAt.get(a.ref) ?? 9999) - (dayAt.get(b.ref) ?? 9999) || (a.startTime ?? "99").localeCompare(b.startTime ?? "99"),
    );
  if (loose.length) groups.push({ key: "none", route: null, color: OFF_ROUTE, stops: loose });

  const numberOf = new Map<string, number>();
  const colorOf = new Map<string, string>();
  for (const g of groups)
    g.stops.forEach((v, i) => {
      numberOf.set(v.ref, i + 1);
      colorOf.set(v.ref, g.color);
    });
  /** Saves a group's new order: on the route for a route (it holds every week), as the day's order otherwise. */
  const saveOrder = (g: Group, refs: string[], done: string) => {
    const on = { onSuccess: () => toast(done), onError: (e: unknown) => toast(errText(e), { kind: "error" }) };
    if (g.route) {
      const jobIds = refs.map((ref) => g.stops.find((v) => v.ref === ref)!.jobId);
      orderRoute.mutate(
        {
          id: g.route.id,
          jobIds: withRiders(
            g.route.jobs.map((j) => j.id),
            jobIds,
          ),
          date,
        },
        on,
      );
    } else {
      const open = refs.filter((ref) => !isDone(g.stops.find((v) => v.ref === ref)));
      if (open.length) saveDay.mutate({ date, refs: open }, on);
    }
  };
  const putOn = (v: VisitCard, r: RouteRow | null) => {
    const on = {
      onSuccess: () => toast(r ? `${v.clientName} is on ${r.name}` : `${v.clientName} is off the route`),
      onError: (e: unknown) => toast(errText(e), { kind: "error" }),
    };
    if (r) orderRoute.mutate({ id: r.id, jobIds: [...r.jobs.map((j) => j.id).filter((id) => id !== v.jobId), v.jobId] }, on);
    else {
      const from = v.routeId ? byId.get(v.routeId)?.r : undefined;
      if (from) orderRoute.mutate({ id: from.id, jobIds: from.jobs.map((j) => j.id).filter((id) => id !== v.jobId) }, on);
    }
  };
  const dropOn = (g: Group, target: string) => (e: DragEvent) => {
    e.preventDefault();
    if (drag?.group !== g.key || drag.ref === target) {
      setDrag(null);
      return;
    }
    const refs = g.stops.map((v) => v.ref).filter((r) => r !== drag.ref);
    refs.splice(refs.indexOf(target) + (dropBelow(e) ? 1 : 0), 0, drag.ref);
    setDrag(null);
    saveOrder(g, refs, "Order saved");
  };
  /** Moves a stop one place up or down, past the next stop still to do (done ones keep their places). */
  const nudge = (g: Group, ref: string, by: -1 | 1) => {
    const refs = g.stops.map((v) => v.ref);
    const at = refs.indexOf(ref);
    const to = at + by;
    if (to < 0 || to >= refs.length || isDone(g.stops[to])) return;
    [refs[at], refs[to]] = [refs[to]!, refs[at]!];
    saveOrder(g, refs, "Order saved");
  };
  const skip = (v: VisitCard) =>
    status.mutate(
      { ref: v.ref, status: "skipped" },
      {
        onSuccess: (d) =>
          toast(`Skipped ${v.clientName} ${shortDate(v.date)}`, {
            undo: () => status.mutate({ ref: d.ref, status: "scheduled" }),
          }),
        onError: (e) => toast(errText(e), { kind: "error" }),
      },
    );

  const total = stops.length;
  const doneCount = stops.filter(isDone).length;
  const nothing = total === 0;

  return (
    <div className="route-day">
      <div className="route-day-head">
        <span className="muted">
          {nothing ? "Nothing booked" : `${plural(total, "stop")}, ${String(doneCount)} done`}
          {live.length ? ` · ${plural(live.length, "route")}` : ""}
        </span>
        {edit ? (
          <div className="row tight">
            <Button type="tertiary" size="small" icon="edit" onClick={() => setManaging("list")}>
              Routes
            </Button>
            <Button type="secondary" size="small" icon="plus" onClick={() => setManaging("new")}>
              New route
            </Button>
          </div>
        ) : null}
      </div>
      {nothing && !groups.length ? (
        <EmptyState icon="schedule" title={`Nothing booked ${dayDate(date)}`}>
          {live.length ? (
            <span className="chips">
              {live.map((r) => {
                const next = nextRun(addDays(date, 1), r);
                return (
                  <Chip key={r.id} pressed={false} onClick={() => onDate(next)}>
                    <span className="dot" style={{ background: routeColor(r, byId.get(r.id)?.i) }} aria-hidden />
                    {r.name}, {shortDate(next)}
                  </Chip>
                );
              })}
            </span>
          ) : (
            "Make a route, then put jobs on it from here."
          )}
        </EmptyState>
      ) : (
        <div className="route-grid">
          <div className="route-list">
            {groups.map((g) => (
              <RouteGroup
                key={g.key}
                g={g}
                date={date}
                start={start}
                edit={edit}
                routes={live}
                people={people.data ?? []}
                hover={hover}
                drag={drag}
                setHover={setHover}
                setDrag={setDrag}
                onDrop={(target) => dropOn(g, target)}
                onOpen={(v) => void navigate({ to: "/visits/$ref", params: { ref: v.ref } })}
                onPutOn={putOn}
                onNudge={(ref, by) => nudge(g, ref, by)}
                onSkip={skip}
                onSaveOrder={(refs) => saveOrder(g, refs, "Best order saved")}
                onMove={() => g.route && setMoving(g.route)}
                onText={() => g.route && setTexting(g.route)}
                onEdit={() => g.route && setManaging(g.route)}
                onDate={onDate}
              />
            ))}
          </div>
          <div className="route-map">
            <RouteMap
              date={date}
              groups={groups}
              active={hover}
              numberOf={(v) => numberOf.get(v.ref) ?? 0}
              colorOf={(v) => colorOf.get(v.ref)}
            />
            {!start && edit ? (
              <p className="muted small">Add where the teams start the day in Schedule settings, for Best order and maps.</p>
            ) : null}
          </div>
        </div>
      )}
      {managing ? (
        <RoutesModal
          start={managing}
          routes={all}
          people={(people.data ?? []).filter((p) => p.active)}
          onClose={() => setManaging(null)}
        />
      ) : null}
      {moving ? <MoveRouteModal route={moving} from={date} onClose={() => setMoving(null)} /> : null}
      {texting ? (
        <Modal title={`Text everyone on ${texting.name}`} open onClose={() => setTexting(null)}>
          <DayText date={date} routeId={texting.id} />
        </Modal>
      ) : null}
    </div>
  );
}

/**
 * The day's map: each route's line along the roads once Google answers, from the start (house pin) and back, the loop Best
 * order plans. Straight dashes stop to stop until then, or without Google.
 */
function RouteMap({
  date,
  groups,
  active,
  numberOf,
  colorOf,
}: {
  date: string;
  groups: Group[];
  active: string | null;
  numberOf: (v: VisitCard) => number;
  colorOf: (v: VisitCard) => string | undefined;
}) {
  const paths = useRoutePaths(
    date,
    groups.map((g) => g.stops),
  );
  const lines: MapLine[] = groups.map((g, i) => {
    const road = paths[i]?.data;
    return road?.roads
      ? { color: g.color, points: road.points, roads: true }
      : { color: g.color, points: g.stops.filter((v) => v.lat !== null && v.lng !== null).map((v) => [v.lat!, v.lng!]) };
  });
  const home = paths.find((p) => p.data?.home)?.data?.home ?? null;
  return (
    <VisitMap
      visits={groups.flatMap((g) => g.stops)}
      active={active}
      numberOf={numberOf}
      colorOf={colorOf}
      lines={lines}
      home={home}
    />
  );
}

const isDone = (v: VisitCard | undefined) => v?.status === "done";
const dropBelow = (e: DragEvent) => {
  const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
  return e.clientY > r.top + r.height / 2;
};

function RouteGroup({
  g,
  date,
  start,
  edit,
  routes,
  people,
  hover,
  drag,
  setHover,
  setDrag,
  onDrop,
  onOpen,
  onPutOn,
  onNudge,
  onSkip,
  onSaveOrder,
  onMove,
  onText,
  onEdit,
  onDate,
}: {
  g: Group;
  date: string;
  start: string;
  edit: boolean;
  routes: RouteRow[];
  people: { id: string; name: string }[];
  hover: string | null;
  drag: { group: string; ref: string } | null;
  setHover: (ref: string | null) => void;
  setDrag: (d: { group: string; ref: string } | null) => void;
  onDrop: (target: string) => (e: DragEvent) => void;
  onOpen: (v: VisitCard) => void;
  onPutOn: (v: VisitCard, r: RouteRow | null) => void;
  onNudge: (ref: string, by: -1 | 1) => void;
  onSkip: (v: VisitCard) => void;
  onSaveOrder: (refs: string[]) => void;
  onMove: () => void;
  onText: () => void;
  onEdit: () => void;
  onDate: (d: string) => void;
}) {
  const best = useBestOrder();
  const toast = useToast();
  const [shown, setShown] = useState<BestOrderAnswer | null>(null);
  const { route } = g;
  const left = g.stops.filter((v) => !isDone(v));
  const done = g.stops.length - left.length;
  const value = g.stops.reduce((s, v) => s + v.totalCents, 0);
  const crew = route ? route.crew.map((id) => people.find((p) => p.id === id)?.name).filter(Boolean) : [];
  const offDay = route && g.stops.length > 0 && route.weekday !== weekdayNumber(date);
  const links = mapsLinks(start, left);
  const findBest = () =>
    best.mutate(
      { date, refs: left.map((v) => v.ref) },
      { onSuccess: setShown, onError: (e) => toast(errText(e), { kind: "error" }) },
    );
  // Fewer minutes, or the same minutes on fewer miles (less gas), is a better order.
  const milesSaved = shown ? Math.max(0, Math.round((shown.now.miles - shown.best.miles) * 10) / 10) : 0;
  const better = !!shown && (shown.savedMinutes > 0 || (shown.savedMinutes === 0 && milesSaved >= 0.5));
  const useBest = () => {
    if (!shown) return;
    // The best order is for the stops still to do; done ones keep their places ahead of them.
    onSaveOrder([...g.stops.filter(isDone).map((v) => v.ref), ...shown.order]);
    setShown(null);
  };

  return (
    <section className="route-group" style={{ ["--rc" as string]: g.color }} aria-label={route?.name ?? "Not on a route"}>
      <header>
        <span className="route-dot" aria-hidden />
        <div className="route-name">
          <b>{route ? route.name : "Not on a route"}</b>
          <span className="muted small">
            {route ? `${WEEKDAYS[route.weekday - 1]!}s` : "Jobs with no route"}
            {crew.length ? ` · ${crew.join(", ")}` : route ? " · No people set" : ""}
            {g.stops.length && edit ? ` · ${money(value)}` : ""}
          </span>
        </div>
        {g.stops.length ? (
          <span className="route-count num">
            {done} of {g.stops.length}
          </span>
        ) : null}
        {edit && left.length > 1 ? (
          <Button type="secondary" size="small" loading={best.isPending} onClick={findBest}>
            Best order
          </Button>
        ) : null}
        {edit && route ? (
          <Menu button={({ toggle }) => <IconButton icon="more" label={`More for ${route.name}`} onClick={toggle} />}>
            {(close) => (
              <>
                {g.stops.length ? (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      close();
                      onMove();
                    }}
                  >
                    Move this day
                  </button>
                ) : null}
                {g.stops.length ? (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      close();
                      onText();
                    }}
                  >
                    Text everyone on it
                  </button>
                ) : null}
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    close();
                    onEdit();
                  }}
                >
                  Edit route
                </button>
              </>
            )}
          </Menu>
        ) : null}
      </header>
      {g.stops.length ? (
        <div className="route-bar" aria-hidden>
          <span style={{ width: `${String(Math.round((done / g.stops.length) * 100))}%` }} />
        </div>
      ) : null}
      {offDay ? (
        <Banner type="notice">
          {route.name} usually runs on {WEEKDAYS[route.weekday - 1]}. These stops were moved here this time.
        </Banner>
      ) : null}
      {shown ? (
        <Banner
          type={better ? "success" : "notice"}
          action={
            <div className="row tight">
              <Button type="tertiary" size="small" onClick={() => setShown(null)}>
                {better ? "Keep mine" : "OK"}
              </Button>
              {better ? (
                <Button size="small" onClick={useBest}>
                  Use this order
                </Button>
              ) : null}
            </div>
          }
        >
          {better
            ? `Saves about ${String(shown.savedMinutes)} min, ${String(milesSaved)} miles and ${String(gallonsFor(milesSaved))} gallons of gas. ${String(shown.best.minutes)} min and ${String(shown.best.miles)} miles of driving instead of ${String(shown.now.minutes)} min and ${String(shown.now.miles)} miles.`
            : `This order is already the shortest, about ${String(shown.now.minutes)} min, ${String(shown.now.miles)} miles and ${String(gallonsFor(shown.now.miles))} gallons of gas.`}
          {shown.estimate ? " A rough guess by straight lines. Add a Google key and a start address for real drive times." : ""}
          {route && better ? ` The new order holds every ${WEEKDAYS[route.weekday - 1]!}.` : ""}
        </Banner>
      ) : null}
      {g.stops.length === 0 && route ? (
        <p className="muted route-empty">
          Nothing due on {route.name} today.{" "}
          <button type="button" className="linkish" onClick={() => onDate(nextRun(addDays(date, 1), route))}>
            Next one {shortDate(nextRun(addDays(date, 1), route))}
          </button>
        </p>
      ) : (
        <ol className="route-stops">
          {g.stops.map((v, i) => (
            <li
              key={v.ref}
              className={`route-stop${isDone(v) ? " done" : ""}${hover === v.ref ? " on" : ""}${drag?.ref === v.ref ? " dragging" : ""}`}
              draggable={edit && !isDone(v)}
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("text/plain", v.ref);
                setDrag({ group: g.key, ref: v.ref });
              }}
              onDragEnd={() => setDrag(null)}
              onDragOver={(e) => {
                if (drag?.group === g.key) e.preventDefault();
              }}
              onDrop={onDrop(v.ref)}
              onMouseEnter={() => setHover(v.ref)}
              onMouseLeave={() => setHover(null)}
            >
              {edit && !isDone(v) ? <Icon name="grip" size={14} label="Drag to change the order" /> : <span />}
              <span className="route-num num">{isDone(v) ? <Icon name="check" size={12} label="Done" /> : i + 1}</span>
              <button type="button" className="route-stop-main" onClick={() => onOpen(v)}>
                <b>{v.clientName}</b>
                <span className="muted small">
                  {[v.address ?? "No address", v.jobName, v.startTime ? timeRange(v.startTime, v.endTime) : null]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </button>
              {v.status === "in_progress" ? <StatusLabel status="informative">Working</StatusLabel> : null}
              {v.moved ? <Icon name="repeat" size={12} label={`Moved from ${shortDate(v.originalDate)}`} /> : null}
              {edit && !isDone(v) ? (
                <span className="route-nudge">
                  <IconButton
                    icon="chevronUp"
                    size={16}
                    label="Move up"
                    disabled={i === 0 || isDone(g.stops[i - 1])}
                    onClick={() => onNudge(v.ref, -1)}
                  />
                  <IconButton
                    icon="chevronDown"
                    size={16}
                    label="Move down"
                    disabled={i === g.stops.length - 1}
                    onClick={() => onNudge(v.ref, 1)}
                  />
                </span>
              ) : null}
              {edit && (routes.length || !isDone(v)) ? (
                <Menu button={({ toggle }) => <IconButton icon="more" label={`More for ${v.clientName}`} onClick={toggle} />}>
                  {(close) => (
                    <>
                      {!isDone(v) ? (
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            close();
                            onSkip(v);
                          }}
                        >
                          Skip this time
                        </button>
                      ) : null}
                      {routes
                        .filter((r) => r.id !== v.routeId)
                        .map((r) => (
                          <button
                            key={r.id}
                            type="button"
                            role="menuitem"
                            onClick={() => {
                              close();
                              onPutOn(v, r);
                            }}
                          >
                            {v.routeId ? "Move to" : "Put on"} {r.name}
                          </button>
                        ))}
                      {v.routeId ? (
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            close();
                            onPutOn(v, null);
                          }}
                        >
                          Take off {route?.name ?? "the route"}
                        </button>
                      ) : null}
                    </>
                  )}
                </Menu>
              ) : null}
            </li>
          ))}
        </ol>
      )}
      {links.length ? (
        <div className="route-links">
          {links.map((l) => (
            <a key={l.href} href={l.href} target="_blank" rel="noreferrer">
              {l.label}
              <Icon name="arrowUpRight" size={12} />
            </a>
          ))}
        </div>
      ) : null}
    </section>
  );
}

/** Move a route's stops from one day to another, this time only, with a text only when the owner writes one. */
export function MoveRouteModal({
  route,
  from,
  to,
  onClose,
}: {
  route: RouteRow;
  from: string;
  to?: string;
  onClose: () => void;
}) {
  const move = useMoveRoute();
  const toast = useToast();
  const [day, setDay] = useState(to ?? addDays(from, 1));
  const [text, setText] = useState(false);
  const [message, setMessage] = useState(
    `Hi {first}, we moved your {service} from ${WEEKDAYS[weekdayNumber(from) - 1]!} to {day}, {date}. Thank you!`,
  );
  return (
    <Modal
      title={`Move ${route.name}`}
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={move.isPending}
            disabled={day === from}
            onClick={() =>
              move.mutate(
                { id: route.id, from, to: day, message: text ? message.trim() || null : null },
                {
                  onSuccess: (x) => {
                    onClose();
                    toast(
                      `Moved ${plural(x.moved, "stop")} to ${dayDate(day)}${x.texted ? `, texted ${String(x.texted)}` : ""}`,
                      {
                        undo: () => move.mutate({ id: route.id, from: day, to: from, message: null }),
                      },
                    );
                  },
                  onError: (e) => toast(errText(e), { kind: "error" }),
                },
              )
            }
          >
            Move
          </Button>
        </>
      }
    >
      <div className="stack">
        <p>
          Moves every stop still to do on {route.name} from {dayDate(from)}. This time only. The route stays on{" "}
          {WEEKDAYS[route.weekday - 1]}s.
        </p>
        <InputText label="Move to" type="date" value={day} onChange={(e) => setDay(e.target.value || day)} />
        <label className="row tight">
          <input type="checkbox" checked={text} onChange={(e) => setText(e.target.checked)} />
          Text the clients about the new day
        </label>
        {text ? (
          <label className="field wide">
            <span className="label">Text. Fill-ins {"{first}, {service}, {day}, {date}, {address}"}</span>
            <textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} />
          </label>
        ) : null}
      </div>
    </Modal>
  );
}

/** Make, change and remove routes: name, weekday, color and people. */
function RoutesModal({
  start,
  routes,
  people,
  onClose,
}: {
  start: RouteRow | "new" | "list";
  routes: RouteRow[];
  people: { id: string; name: string }[];
  onClose: () => void;
}) {
  const add = useAddRoute();
  const change = useChangeRoute();
  const remove = useRemoveRoute();
  const toast = useToast();
  const blank = (): RouteInput & { id?: string } => ({
    name: "",
    weekday: 1,
    crew: [],
    color: ROUTE_COLORS[routes.length % ROUTE_COLORS.length]!,
  });
  const [form, setForm] = useState<(RouteInput & { id?: string }) | null>(
    start === "new"
      ? blank()
      : start === "list"
        ? null
        : { id: start.id, name: start.name, weekday: start.weekday, crew: start.crew, color: start.color ?? ROUTE_COLORS[0]! },
  );
  const [sure, setSure] = useState(false);
  const on = (done: string) => ({
    onSuccess: () => {
      toast(done);
      setForm(null);
      setSure(false);
      if (start !== "list") onClose();
    },
    onError: (e: unknown) => toast(errText(e), { kind: "error" }),
  });
  const save = () => {
    if (!form) return;
    const { id, ...body } = form;
    if (id) change.mutate({ id, patch: body }, on("Route saved"));
    else add.mutate(body, on(`${body.name} is ready. Put jobs on it from the Route view.`));
  };
  const editing = form?.id ? routes.find((r) => r.id === form.id) : undefined;

  return (
    <Modal
      title={form ? (form.id ? `Edit ${editing?.name ?? "route"}` : "New route") : "Routes"}
      open
      onClose={onClose}
      footer={
        form ? (
          <>
            {form.id ? (
              sure ? (
                <Button
                  variation="destructive"
                  loading={remove.isPending}
                  onClick={() => remove.mutate(form.id!, on("Route removed"))}
                >
                  Remove, its {plural(editing?.jobs.length ?? 0, "job")} stay
                </Button>
              ) : (
                <Button type="tertiary" onClick={() => setSure(true)}>
                  Remove route
                </Button>
              )
            ) : null}
            <span className="route-grow" />
            <Button type="tertiary" onClick={() => (start === "list" ? setForm(null) : onClose())}>
              Cancel
            </Button>
            <Button loading={add.isPending || change.isPending} disabled={!form.name.trim()} onClick={save}>
              Save route
            </Button>
          </>
        ) : (
          <Button type="secondary" icon="plus" onClick={() => setForm(blank())}>
            New route
          </Button>
        )
      }
    >
      {form ? (
        <div className="stack">
          <InputText label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <fieldset className="route-pick">
            <legend className="label">Runs on</legend>
            <div className="chips">
              {WEEKDAYS.map((w, i) => (
                <Chip key={w} pressed={form.weekday === i + 1} onClick={() => setForm({ ...form, weekday: i + 1 })}>
                  {w.slice(0, 3)}
                </Chip>
              ))}
            </div>
          </fieldset>
          <fieldset className="route-pick">
            <legend className="label">Color</legend>
            <div className="row tight">
              {ROUTE_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className="route-swatch"
                  style={{ background: c }}
                  aria-pressed={form.color === c}
                  aria-label={`Color ${c}`}
                  onClick={() => setForm({ ...form, color: c })}
                />
              ))}
            </div>
          </fieldset>
          <fieldset className="route-pick">
            <legend className="label">People who run it</legend>
            <span className="muted small">They work every stop on it, unless a job or visit has its own people.</span>
            <div className="chips">
              {people.map((p) => {
                const crew = form.crew;
                const has = crew.includes(p.id);
                return (
                  <Chip
                    key={p.id}
                    pressed={has}
                    onClick={() => setForm({ ...form, crew: has ? crew.filter((x) => x !== p.id) : [...crew, p.id] })}
                  >
                    {p.name}
                  </Chip>
                );
              })}
              {!people.length ? <span className="muted small">No one on the team yet.</span> : null}
            </div>
          </fieldset>
        </div>
      ) : routes.length ? (
        <ul className="auto-list">
          {routes.map((r, i) => (
            <li key={r.id} className="setting-row">
              <span className="what">
                <b>
                  <span className="dot" style={{ background: routeColor(r, i) }} aria-hidden /> {r.name}
                </b>
                <span>
                  {WEEKDAYS[r.weekday - 1]}s, {plural(r.jobs.length, "job")}
                  {r.crew.length
                    ? `, ${r.crew
                        .map((id) => people.find((p) => p.id === id)?.name)
                        .filter(Boolean)
                        .join(", ")}`
                    : ""}
                </span>
              </span>
              <Button
                type="tertiary"
                size="small"
                icon="edit"
                onClick={() =>
                  setForm({ id: r.id, name: r.name, weekday: r.weekday, crew: r.crew, color: r.color ?? routeColor(r, i) })
                }
              >
                Edit
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">No routes yet. A route runs on a weekday with its stops in order.</p>
      )}
    </Modal>
  );
}
